import { Database } from '../../types/supabase';
import { formatPeriod, getTargetPeriod } from '../../utils/periodUtils';
export type ReviewDocument =
  Database['public']['Functions']['get_all_documents']['Returns'][number];
export type ReviewStatus =
  | 'pending'
  | 'all'
  | 'overdue'
  | 'completed'
  | 'rejected';
export const statusOptions: { value: ReviewStatus; label: string }[] = [
  { value: 'pending', label: 'Pendientes de aprobación' },
  { value: 'overdue', label: 'Expirados' },
  { value: 'completed', label: 'Aprobados' },
  { value: 'rejected', label: 'Rechazados' },
  { value: 'all', label: 'Todos los documentos' },
];
export function readReviewFilters(params: URLSearchParams) {
  const status = statusOptions.some(
    (item) => item.value === params.get('status'),
  )
    ? (params.get('status') as ReviewStatus)
    : 'pending';
  const user = params.get('user') || 'all';
  const org = user !== 'all' ? 'all' : params.get('org') || 'all';
  const ai = ['human_approved','pass','issue','review','queued','running','manual_review','failed','none'].includes(params.get('ai') || '') ? params.get('ai')! : 'all';
  const sort = ['newest','expiry','user','document'].includes(params.get('sort') || '') ? params.get('sort')! : 'oldest';
  return { status, user, org, type: params.get('type') || 'all', ai, sort };
}
export type ReviewFilters = Omit<ReturnType<typeof readReviewFilters>, 'ai' | 'sort'> & {ai?:string;sort?:string};
export const PAGE_SIZE = 25;
/** Common reviewer views. Each chip only sets the existing `ai` filter. */
export const QUICK_AI_FILTERS: { ai: string; label: string; hint: string }[] = [
  { ai: 'all', label: 'Todos', hint: 'Sin filtrar por estado de IA' },
  { ai: 'issue', label: 'Recomienda rechazar', hint: 'La IA detectó un incumplimiento; decide el revisor' },
  { ai: 'review', label: 'Por resolver', hint: 'La IA no pudo concluir' },
  { ai: 'manual_review', label: 'Manual', hint: 'El archivo requiere revisión manual' },
  { ai: 'pass', label: 'Recomienda aprobar', hint: 'Recomendación de IA; requiere aprobación humana' },
];
/** Returns the URL params for a quick view, keeping every other filter and restarting pagination. */
export function quickFilterParams(current: URLSearchParams, ai: string) {
  const next = new URLSearchParams(current);
  next.delete('batches');
  if (ai === 'all') next.delete('ai');
  else next.set('ai', ai);
  return next;
}
export function isExpiredDocument(
  doc: Pick<ReviewDocument, 'expiry_date'>,
  now = new Date(),
) {
  if (!doc.expiry_date) return false;
  // Date-only expiries remain valid throughout that local calendar date.
  if (/^\d{4}-\d{2}-\d{2}$/.test(doc.expiry_date)) {
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return doc.expiry_date < today;
  }
  return new Date(doc.expiry_date).getTime() < now.getTime();
}
export function documentPeriod(doc: ReviewDocument) {
  return getTargetPeriod({
    applicableMonth: doc.applicable_month,
    applicableYear: doc.applicable_year,
    dueDate: doc.due_date,
    uploadDate: doc.upload_date,
    renewalType: doc.renewal_type,
    renewalInterval: doc.renewal_interval as string | null,
  });
}
export function reviewDocumentUrl(doc: ReviewDocument, returnTo: string) {
  const { month, year } = documentPeriod(doc);
  return `/review/${encodeURIComponent(doc.user_id)}?${new URLSearchParams({ selectedMonth: formatPeriod(month, year), documentId: doc.document_id, returnTo })}`;
}
export function getReviewReturnTo(value: string | null): string | null {
  if (!value?.startsWith('/')) return null;
  try {
    const url = new URL(value, 'https://repse.invalid');
    return url.origin === 'https://repse.invalid' && url.pathname in RETURN_LABELS
      ? `${url.pathname}${url.search}${url.hash}`
      : null;
  } catch {
    return null;
  }
}

/** Screens a document review can return to, with the label of their back button. */
const RETURN_LABELS: Record<string, string> = {
  '/review': 'Volver a resultados',
  '/admin/users': 'Volver a usuarios',
  '/admin/activity': 'Volver a actividad',
  '/admin/dashboard': 'Volver al resumen',
  '/report': 'Volver al reporte',
};

export function returnLabel(returnTo: string | null): string {
  if (!returnTo) return RETURN_LABELS['/admin/users'];
  return RETURN_LABELS[new URL(returnTo, 'https://repse.invalid').pathname] || 'Volver';
}
export function documentStatus(
  doc: Pick<ReviewDocument, 'status' | 'rejection_reason'>,
) {
  if (
    doc.status === 'pending_approval' &&
    doc.rejection_reason?.startsWith('SOLICITUD DE REVISIÓN:')
  )
    return {
      label: 'Revisión solicitada',
      color: 'bg-orange-100 text-orange-900',
    };
  const labels: Record<string, { label: string; color: string }> = {
    pending_upload: {
      label: 'Pendiente de carga',
      color: 'bg-slate-100 text-slate-700',
    },
    pending_approval: {
      label: 'Pendiente de aprobación',
      color: 'bg-amber-100 text-amber-900',
    },
    approved: { label: 'Aprobado', color: 'bg-green-100 text-green-900' },
    rejected: { label: 'Rechazado', color: 'bg-red-100 text-red-900' },
    expired: { label: 'Vencido', color: 'bg-red-100 text-red-900' },
    superseded: { label: 'Sustituido', color: 'bg-slate-100 text-slate-700' },
  };
  return (
    labels[doc.status] || {
      label: 'Estado no disponible',
      color: 'bg-slate-100 text-slate-700',
    }
  );
}
